import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export type AccountType = "worker" | "employer";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  type: AccountType;
  avatar: string;
}

interface StoredAccount {
  id: string;
  name: string;
  email: string;
  password: string;
  type: AccountType;
  avatar: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => AuthUser | null;
  signup: (name: string, email: string, password: string, type: AccountType) => AuthUser | null;
  logout: () => void;
}

const SESSION_KEY = "gigncash_session";
const ACCOUNTS_KEY = "gigncash_accounts";

const WORKER_AVATARS = [
  "https://public.readdy.ai/ai/img_res/c6e13c8bffe62ebe3f145ac2d3d1dee3.jpg",
  "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20black%20man%20smiling%20confident%20clean%20white%20background%20studio%20lighting%20sharp%20focus%20high%20resolution&width=120&height=120&seq=aw2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20woman%20smiling%20confident%20clean%20white%20background%20studio%20lighting%20sharp%20focus%20high%20resolution&width=120&height=120&seq=aw3&orientation=squarish",
];

const EMPLOYER_AVATARS = [
  "https://readdy.ai/api/search-image?query=professional%20headshot%20confident%20asian%20businesswoman%20smiling%20clean%20office%20background%20sharp%20focus%20studio%20lighting%20high%20resolution&width=120&height=120&seq=ae1&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20headshot%20confident%20middle%20aged%20businessman%20smiling%20clean%20office%20background%20sharp%20focus%20studio%20lighting&width=120&height=120&seq=ae2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=professional%20headshot%20confident%20woman%20executive%20smiling%20clean%20office%20background%20sharp%20focus%20studio%20lighting&width=120&height=120&seq=ae3&orientation=squarish",
];

const DEMO_ACCOUNTS: StoredAccount[] = [
  {
    id: "w-001",
    name: "Marcus Rivera",
    email: "marcus@example.com",
    password: "demo123",
    type: "worker",
    avatar: WORKER_AVATARS[0],
  },
  {
    id: "e-001",
    name: "Sarah Chen",
    email: "sarah@buildfast.co",
    password: "demo123",
    type: "employer",
    avatar: EMPLOYER_AVATARS[0],
  },
];

function getAllAccounts(): StoredAccount[] {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    const userAccounts: StoredAccount[] = raw ? JSON.parse(raw) : [];
    const userEmails = new Set(userAccounts.map((a) => a.email.toLowerCase()));
    const demosToAdd = DEMO_ACCOUNTS.filter((d) => !userEmails.has(d.email.toLowerCase()));
    return [...userAccounts, ...demosToAdd];
  } catch {
    return [...DEMO_ACCOUNTS];
  }
}

function persistNewAccount(account: StoredAccount) {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    const existing: StoredAccount[] = raw ? JSON.parse(raw) : [];
    existing.push(account);
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(existing));
  } catch {
    // ignore storage errors
  }
}

function toAuthUser(a: StoredAccount): AuthUser {
  return { id: a.id, name: a.name, email: a.email, type: a.type, avatar: a.avatar };
}

function readSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(readSession);

  useEffect(() => {
    if (user) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
  }, [user]);

  const login = (email: string, password: string): AuthUser | null => {
    const accounts = getAllAccounts();
    const match = accounts.find(
      (a) =>
        a.email.toLowerCase() === email.toLowerCase() && a.password === password,
    );
    if (!match) return null;
    const authUser = toAuthUser(match);
    setUser(authUser);
    return authUser;
  };

  const signup = (
    name: string,
    email: string,
    password: string,
    type: AccountType,
  ): AuthUser | null => {
    const accounts = getAllAccounts();
    const emailTaken = accounts.some(
      (a) => a.email.toLowerCase() === email.toLowerCase(),
    );
    if (emailTaken) return null;

    const pool = type === "employer" ? EMPLOYER_AVATARS : WORKER_AVATARS;
    const avatar = pool[Math.floor(Math.random() * pool.length)];

    const newAccount: StoredAccount = {
      id: `${type[0]}-${Date.now()}`,
      name,
      email,
      password,
      type,
      avatar,
    };

    persistNewAccount(newAccount);
    const authUser = toAuthUser(newAccount);
    setUser(authUser);
    return authUser;
  };

  const logout = () => setUser(null);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn: !!user, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used inside <AuthProvider>");
  }
  return ctx;
}
